/**
 * The rules of the fingers on the world (visual spec, Camera; decision 0021), the same as the
 * app's `TouchInterpreter` in `apps/ios/Packages/MistsweeperRender`: a touch that moves no more
 * than 8 points and lifts within 380 ms is a tap, one held 380 ms without moving is a hold, one
 * that moves further drags the world. Two fingers decide what they do once their midpoint has
 * travelled 14 points, their spread has changed by 14, or they have turned through an arc of 14:
 * side by side and moving up or down together they tilt the camera; anything else pans by their
 * midpoint, and zooms and turns around it, so the ground under both stays under them. When one of
 * two lifts, the other drags on. A touch that comes down on a world moving by itself only stops
 * it: no tap and no hold (Daniel, 2026-10-21).
 *
 * Positions come in CSS pixels and leave in device pixels. The hold is timed by the caller: when
 * `holdAt` is set, a timer at that moment calls `holdDue()`. Pure, so tests drive it with touches
 * and times of their own.
 */

/** A finger on the world: an id that stays its own while it is down, and where it is. */
export interface TouchPoint {
  readonly id: number;
  readonly x: number;
  readonly y: number;
}

/** What the fingers ask of the world, in device pixels. */
export type TouchAction =
  /** A short touch that stayed put: reveal or chord, at the point where it lifted. */
  | { readonly kind: "tap"; readonly x: number; readonly y: number }
  /** A touch held still: plant a banner or take it away, at the point where it came down. */
  | { readonly kind: "hold"; readonly x: number; readonly y: number }
  /** A finger began to drag the world, or a second one came down. */
  | { readonly kind: "panBegan" }
  /** The world dragged, by a finger or by the midpoint of two. */
  | { readonly kind: "pan"; readonly dx: number; readonly dy: number }
  /** The last finger lifted after a drag: the world may glide on. */
  | { readonly kind: "panEnded" }
  /** Two fingers spreading or closing by a factor, around their midpoint. */
  | { readonly kind: "pinch"; readonly factor: number; readonly x: number; readonly y: number }
  /** Two fingers turning around their midpoint, in radians, positive clockwise on the screen. */
  | { readonly kind: "twist"; readonly radians: number; readonly x: number; readonly y: number }
  /** Two fingers side by side moving up or down together, positive down. */
  | { readonly kind: "tilt"; readonly dy: number }
  /** Two fingers became one: the gesture of two is over, and the finger left drags on. */
  | { readonly kind: "pinchEnded" };

/** How long a touch is held still before it is a hold, in milliseconds. */
export const HOLD_MS = 380;
/** How far a touch moves before it drags, in points. */
export const DRAG_POINTS = 8;
/** How far two fingers travel before what they do is decided, in points. */
export const DECIDE_POINTS = 14;

interface Finger {
  readonly id: number;
  x: number;
  y: number;
}

/** The finger that taps, holds and drags: the first down, or the one left of two. */
interface Primary {
  readonly id: number;
  readonly x0: number;
  readonly y0: number;
  lastX: number;
  lastY: number;
  readonly downAt: number;
  dragging: boolean;
  held: boolean;
  /** It came down on a world moving by itself, and only stopped it. */
  readonly caught: boolean;
}

/** What two fingers do, decided by their first movement. */
type Gesture = "undecided" | "tilt" | "free";

/**
 * Two fingers: the first two down. Where they were at the last move, how far they have travelled
 * while undecided, and what they do.
 */
interface Pair {
  readonly a: number;
  readonly b: number;
  distance: number;
  angle: number;
  midX: number;
  midY: number;
  travelX: number;
  travelY: number;
  spread: number;
  turn: number;
  gesture: Gesture;
}

export class TouchInterpreter {
  /** The fingers down, in the order they came down. */
  private readonly fingers: Finger[] = [];
  private primary: Primary | null = null;
  private pair: Pair | null = null;
  private hold: number | null = null;
  /** Device pixels per CSS pixel, by which positions leave. */
  pixelsPerPoint: number;

  /** An interpreter for a screen of `pixelsPerPoint` device pixels per CSS pixel. */
  constructor(pixelsPerPoint: number) {
    this.pixelsPerPoint = pixelsPerPoint;
  }

  /**
   * When the touch down now becomes a hold, in milliseconds on the clock the touches come with;
   * null while no hold is waiting.
   */
  get holdAt(): number | null {
    return this.hold;
  }

  /** Whether fingers are moving the world: a drag or a gesture of two is under way. */
  get moving(): boolean {
    return this.pair !== null || this.primary?.dragging === true;
  }

  /** Whether any finger is down. */
  get touching(): boolean {
    return this.fingers.length > 0;
  }

  /**
   * A finger came down at `t` milliseconds. `catching` when it is the first and came down on a
   * world moving by itself, which it only stops: it may drag on, but is no tap and no hold.
   */
  began(touch: TouchPoint, t: number, catching = false): TouchAction[] {
    if (this.fingers.some((f) => f.id === touch.id)) return [];
    this.fingers.push({ id: touch.id, x: touch.x, y: touch.y });
    if (this.fingers.length === 1) {
      this.primary = {
        id: touch.id,
        x0: touch.x,
        y0: touch.y,
        lastX: touch.x,
        lastY: touch.y,
        downAt: t,
        dragging: false,
        held: false,
        caught: catching,
      };
      this.hold = catching ? null : t + HOLD_MS;
      return [];
    }
    // A third finger changes nothing.
    if (this.fingers.length > 2) return [];
    // A second finger: no tap and no hold any more, and the first finger drags.
    this.hold = null;
    this.pair = this.makePair("undecided");
    const first = this.primary;
    if (!first || first.dragging) return [];
    first.dragging = true;
    return [{ kind: "panBegan" }];
  }

  /** Fingers moved, to where they are now. */
  moved(touches: readonly TouchPoint[]): TouchAction[] {
    let changed = false;
    for (const touch of touches) {
      const finger = this.fingers.find((f) => f.id === touch.id);
      if (!finger) continue;
      finger.x = touch.x;
      finger.y = touch.y;
      changed = true;
    }
    if (!changed) return [];
    if (this.pair) return this.movePair(this.pair);
    const first = this.primary;
    const finger = first && this.fingers.find((f) => f.id === first.id);
    if (!first || !finger) return [];
    const out: TouchAction[] = [];
    if (!first.dragging && Math.hypot(finger.x - first.x0, finger.y - first.y0) > DRAG_POINTS) {
      first.dragging = true;
      this.hold = null;
      out.push({ kind: "panBegan" });
    }
    // From where it was at the last move, so the ground lags the finger by the few points it
    // moved before it dragged and never jumps to catch up.
    if (first.dragging && (finger.x !== first.lastX || finger.y !== first.lastY)) {
      const s = this.pixelsPerPoint;
      out.push({ kind: "pan", dx: (finger.x - first.lastX) * s, dy: (finger.y - first.lastY) * s });
    }
    first.lastX = finger.x;
    first.lastY = finger.y;
    return out;
  }

  /** A finger lifted at `t` milliseconds, at `touch`. */
  ended(touch: TouchPoint, t: number): TouchAction[] {
    const finger = this.fingers.find((f) => f.id === touch.id);
    if (finger) {
      finger.x = touch.x;
      finger.y = touch.y;
    }
    return this.lift(touch.id, t, false);
  }

  /**
   * The system took a finger away, at `t` milliseconds: it ends what it did, as a finger lifting
   * does, but is never a tap or a hold.
   */
  cancelled(id: number, t: number): TouchAction[] {
    return this.lift(id, t, true);
  }

  /**
   * The hold's timer fired, at `holdAt`: the finger down is a hold where it came down, unless it
   * has dragged or a second finger has come since.
   */
  holdDue(): TouchAction[] {
    if (this.hold === null) return [];
    this.hold = null;
    const first = this.primary;
    if (!first || first.dragging || first.held || this.pair) return [];
    first.held = true;
    const s = this.pixelsPerPoint;
    return [{ kind: "hold", x: first.x0 * s, y: first.y0 * s }];
  }

  /** A pair of the first two fingers down, as they lie now, doing `gesture`. */
  private makePair(gesture: Gesture): Pair | null {
    const [a, b] = this.fingers;
    if (!a || !b) return null;
    return {
      a: a.id,
      b: b.id,
      distance: Math.hypot(b.x - a.x, b.y - a.y),
      angle: Math.atan2(b.y - a.y, b.x - a.x),
      midX: (a.x + b.x) / 2,
      midY: (a.y + b.y) / 2,
      travelX: 0,
      travelY: 0,
      spread: 0,
      turn: 0,
      gesture,
    };
  }

  /**
   * Two fingers moved: undecided until they have travelled a little, then a tilt, or a pan, zoom
   * and turn around their midpoint.
   */
  private movePair(pair: Pair): TouchAction[] {
    const [a, b] = this.fingers;
    if (!a || !b) return [];
    const d = Math.hypot(b.x - a.x, b.y - a.y);
    const angle = Math.atan2(b.y - a.y, b.x - a.x);
    const midX = (a.x + b.x) / 2;
    const midY = (a.y + b.y) / 2;
    let turn = angle - pair.angle;
    if (turn > Math.PI) turn -= 2 * Math.PI;
    if (turn < -Math.PI) turn += 2 * Math.PI;
    if (pair.gesture === "undecided") {
      // Fingers side by side that move up or down together tilt; anything else pans, zooms and
      // turns. Decided once the midpoint or the spread has moved a little, or the fingers have
      // turned through as long an arc.
      pair.travelX += midX - pair.midX;
      pair.travelY += midY - pair.midY;
      pair.spread += d - pair.distance;
      pair.turn += turn;
      const moved = Math.hypot(pair.travelX, pair.travelY);
      const limit = DECIDE_POINTS;
      if (moved > limit || Math.abs(pair.spread) > limit || (Math.abs(pair.turn) * d) / 2 > limit) {
        const sideBySide = Math.abs(b.x - a.x) > Math.abs(b.y - a.y);
        const tilt =
          moved > limit &&
          sideBySide &&
          Math.abs(pair.travelY) > 2 * Math.abs(pair.travelX) &&
          Math.abs(pair.spread) < moved * 0.5;
        pair.gesture = tilt ? "tilt" : "free";
      }
    }
    const s = this.pixelsPerPoint;
    const out: TouchAction[] = [];
    if (pair.gesture === "tilt") {
      if (midY !== pair.midY) out.push({ kind: "tilt", dy: (midY - pair.midY) * s });
    } else if (pair.gesture === "free") {
      // The ground under the old midpoint moves to the new one, then zooms and turns around it,
      // so the ground under both fingers stays under them.
      if (midX !== pair.midX || midY !== pair.midY) {
        out.push({ kind: "pan", dx: (midX - pair.midX) * s, dy: (midY - pair.midY) * s });
      }
      if (pair.distance > 0 && d > 0 && d !== pair.distance) {
        out.push({ kind: "pinch", factor: d / pair.distance, x: midX * s, y: midY * s });
      }
      if (turn !== 0) out.push({ kind: "twist", radians: turn, x: midX * s, y: midY * s });
    }
    pair.distance = d;
    pair.angle = angle;
    pair.midX = midX;
    pair.midY = midY;
    return out;
  }

  /** A finger lifted or was taken away: it ends a gesture of two, or a drag, a tap or a hold. */
  private lift(id: number, t: number, cancelled: boolean): TouchAction[] {
    const i = this.fingers.findIndex((f) => f.id === id);
    const finger = this.fingers[i];
    if (!finger) return [];
    this.fingers.splice(i, 1);
    const [a, b] = this.fingers;
    if (this.pair) {
      if (a && b) {
        // A third finger stays: the first two down make the pair from now on, from where they
        // lie, still doing what was decided.
        if (this.pair.a !== a.id || this.pair.b !== b.id) {
          this.pair = this.makePair(this.pair.gesture);
        }
        if (this.primary?.id === id) this.primary = dragging(a, t);
        return [];
      }
      // One finger is left, and it drags the world on from where it is.
      this.pair = null;
      this.primary = a ? dragging(a, t) : null;
      return [{ kind: "pinchEnded" }];
    }
    const first = this.primary;
    if (!first || first.id !== id) return [];
    this.primary = null;
    this.hold = null;
    if (first.dragging) return [{ kind: "panEnded" }];
    if (cancelled || first.held || first.caught) return [];
    const s = this.pixelsPerPoint;
    // A hold whose timer came late, behind a busy main thread, still counts as a hold.
    if (t - first.downAt >= HOLD_MS) return [{ kind: "hold", x: first.x0 * s, y: first.y0 * s }];
    return [{ kind: "tap", x: finger.x * s, y: finger.y * s }];
  }
}

/** A finger that drags the world from where it is. */
function dragging(finger: Finger, t: number): Primary {
  return {
    id: finger.id,
    x0: finger.x,
    y0: finger.y,
    lastX: finger.x,
    lastY: finger.y,
    downAt: t,
    dragging: true,
    held: false,
    caught: false,
  };
}
